import { useEffect, useState } from "react";
import axios from "axios";
import { getDeviceId } from "@/lib/deviceId";
import { toast } from "sonner";
import { ShieldCheck, Delete, Store, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "back"];

/**
 * Πύλη Συσκευής (Ιούν 2026). Full-screen gate που εμφανίζεται ΠΡΙΝ το
 * login: μη καταχωρημένες συσκευές βλέπουν μόνο την οθόνη «Καταχώρηση
 * Συσκευής» (κωδικός καταστήματος + 8-ψήφιος κωδικός). Το πέρασμα
 * θυμάται ο browser (localStorage `gate_tenant`) — η πραγματική
 * επιβολή γίνεται server-side στο /auth/login (device trust).
 */
export default function DeviceGate({ children }) {
  const [phase, setPhase] = useState("checking"); // checking|tenant|code|passed
  const [tenant, setTenant] = useState(
    () => localStorage.getItem("gate_tenant") || localStorage.getItem("tenant_code") || ""
  );
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const pass = (t) => {
    localStorage.setItem("gate_tenant", t);
    localStorage.setItem("tenant_code", t);
    setPhase("passed");
  };

  const check = async (tc, { silent = false } = {}) => {
    const t = (tc || "").trim().toUpperCase();
    if (!t) {
      setPhase("tenant");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/devices/gate-status`, {
        tenant_code: t,
        device_id: getDeviceId(),
      });
      const d = r.data || {};
      if (!d.gate_enabled || d.status === "trusted") {
        pass(t);
      } else {
        setTenant(t);
        setPhase("code");
        if (!silent && d.status === "locked") {
          toast.error("Η συσκευή κλειδώθηκε — χρειάζεται νέος κωδικός από τον διαχειριστή");
        }
      }
    } catch (e) {
      const st = e.response?.status;
      if (silent) {
        if (st === 401 || st === 403 || st === 404) {
          localStorage.removeItem("gate_tenant");
          setPhase("tenant");
        } else {
          // Network blip: fail-open — η επιβολή γίνεται ούτως ή άλλως
          // server-side στο login.
          setPhase("passed");
        }
      } else {
        toast.error(e.response?.data?.detail || "Λάθος κωδικός καταστήματος");
        setPhase("tenant");
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // QR auto-registration (Ιούν 2026): ?gate_tenant=X&gate_code=12345678
    const sp = new URLSearchParams(window.location.search);
    const qCode = (sp.get("gate_code") || "").trim();
    const qTenant = (sp.get("gate_tenant") || "").trim().toUpperCase();
    if (qCode.length === 8) {
      const t =
        qTenant ||
        localStorage.getItem("gate_tenant") ||
        localStorage.getItem("tenant_code") ||
        "";
      window.history.replaceState({}, "", window.location.pathname);
      if (t) {
        setTenant(t);
        setPhase("code");
        redeemFor(t, qCode);
        return;
      }
    }
    const t = localStorage.getItem("gate_tenant") || localStorage.getItem("tenant_code") || "";
    if (t) check(t, { silent: true });
    else setPhase("tenant");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redeemFor = async (t, finalCode) => {
    setBusy(true);
    try {
      await axios.post(`${API}/devices/gate-redeem`, {
        tenant_code: t.trim().toUpperCase(),
        code: finalCode,
        device_id: getDeviceId(),
        label: label.trim() || "Συσκευή",
      });
      toast.success("Η συσκευή καταχωρήθηκε!");
      pass(t.trim().toUpperCase());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Άκυρος κωδικός");
      setCode("");
      setPhase("code");
    } finally {
      setBusy(false);
    }
  };

  const redeem = async (val) => {
    const finalCode = (val ?? code).trim();
    if (finalCode.length !== 8) {
      toast.error("Ο κωδικός συσκευής είναι 8 ψηφία");
      return;
    }
    await redeemFor(tenant, finalCode);
  };

  const press = (k) => {
    if (busy) return;
    if (k === "C") return setCode("");
    if (k === "back") return setCode((v) => v.slice(0, -1));
    const next = (code + k).slice(0, 8);
    setCode(next);
    if (next.length === 8) redeem(next);
  };

  if (phase === "passed") return children;

  return (
    <div className="h-screen w-screen grid place-items-center bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 p-4">
      <div className="w-full max-w-[440px] rounded-2xl shadow-2xl bg-white p-6" data-testid="device-gate">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-full bg-cyan-100 grid place-items-center">
            <ShieldCheck className="text-cyan-700" size={26} />
          </div>
          <div>
            <div className="text-lg font-bold text-slate-800">Καταχώρηση Συσκευής</div>
            <div className="text-xs text-slate-500">
              Η πρόσβαση επιτρέπεται μόνο από εγκεκριμένες συσκευές
            </div>
          </div>
        </div>

        {phase === "checking" && (
          <div className="py-10 text-center text-slate-400 text-sm" data-testid="gate-checking">
            Έλεγχος συσκευής…
          </div>
        )}

        {phase === "tenant" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              check(tenant);
            }}
          >
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5 mb-1">
              <Store size={13} /> Κωδικός Καταστήματος
            </label>
            <input
              type="text"
              value={tenant}
              onChange={(e) => setTenant(e.target.value.toUpperCase())}
              placeholder="π.χ. TAVERNA_KOSTAS"
              autoFocus
              data-testid="gate-tenant-input"
              className="w-full h-12 px-3 rounded-lg border-2 border-slate-300 font-mono text-lg outline-none focus:border-cyan-600 mb-4"
            />
            <button
              type="submit"
              disabled={busy || !tenant.trim()}
              data-testid="gate-continue-btn"
              className="w-full h-12 rounded-lg bg-cyan-700 hover:bg-cyan-800 text-white font-bold flex items-center justify-center gap-2 disabled:bg-slate-300"
            >
              {busy ? "Έλεγχος…" : <>Συνέχεια <ArrowRight size={18} /></>}
            </button>
          </form>
        )}

        {phase === "code" && (
          <>
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 mb-3 text-xs text-slate-600 flex justify-between items-center">
              <span>
                Κατάστημα: <b className="font-mono">{tenant}</b>
              </span>
              <button
                type="button"
                onClick={() => {
                  setCode("");
                  setPhase("tenant");
                }}
                data-testid="gate-change-tenant"
                className="text-cyan-700 font-bold hover:underline"
              >
                Αλλαγή
              </button>
            </div>
            <div className="text-xs text-slate-600 mb-2">
              Ζήτησε από τον διαχειριστή έναν 8-ψήφιο <b>Κωδικό Συσκευής</b> (Ρυθμίσεις →
              Συσκευές). Θα ζητηθεί <b>μόνο μία φορά</b> σε αυτή τη συσκευή.
            </div>
            <div className="rounded-xl bg-slate-100 px-4 py-3 mb-2">
              <div
                className="h-10 flex items-center justify-center text-3xl font-mono tracking-[0.35em] font-bold"
                data-testid="gate-code-display"
              >
                {code ? (
                  code.padEnd(8, "•").split("").map((c, i) => (
                    <span key={i} className={i < code.length ? "text-slate-800" : "text-slate-300"}>
                      {c}
                    </span>
                  ))
                ) : (
                  <span className="text-slate-400 text-base font-normal tracking-normal">••••••••</span>
                )}
              </div>
            </div>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 40))}
              placeholder="Όνομα συσκευής (π.χ. Ταμείο Α)"
              data-testid="gate-label-input"
              className="w-full h-9 mb-3 px-3 rounded-md bg-white border border-slate-300 text-sm outline-none focus:border-cyan-600"
            />
            <div className="grid grid-cols-3 gap-2">
              {KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => press(k)}
                  disabled={busy}
                  data-testid={`gate-key-${k}`}
                  className={`h-12 rounded-lg border-2 text-xl font-bold font-mono active:scale-[0.96] transition-transform ${
                    k === "C"
                      ? "bg-red-600 border-red-700 text-white hover:bg-red-700"
                      : k === "back"
                      ? "bg-slate-200 border-slate-300 hover:bg-slate-300"
                      : "bg-white border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {k === "back" ? <Delete size={20} className="mx-auto" /> : k}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => redeem()}
              disabled={busy || code.length !== 8}
              data-testid="gate-register-btn"
              className="w-full mt-3 h-12 rounded-lg bg-cyan-700 hover:bg-cyan-800 text-white font-bold disabled:bg-slate-300"
            >
              {busy ? "Καταχώρηση…" : "Καταχώρηση Συσκευής"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
